import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Database,
  Globe,
  Shield,
  BarChart3,
  Zap,
  Settings,
  CheckCircle,
  ArrowRight,
  TrendingUp,
  Cloud,
  Lock,
  Server,
  Snowflake,
  Building,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";

const CloudPlatformsDetail = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate("/");
    setTimeout(() => {
      document
        .getElementById("contact")
        ?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const cloudServices = [
    {
      platform: "Oracle Cloud Infrastructure (OCI)",
      color: "bg-primary",
      services: [
        {
          icon: Database,
          name: "Autonomous Database",
          description:
            "Self-driving, self-securing, self-repairing database services",
        },
        {
          icon: Globe,
          name: "Container Engine",
          description:
            "Kubernetes orchestration and containerized application deployment",
        },
        {
          icon: Shield,
          name: "Security Services",
          description:
            "Identity management, data encryption, and network security",
        },
        {
          icon: BarChart3,
          name: "Analytics Cloud",
          description:
            "Business intelligence, data visualization, and machine learning",
        },
      ],
    },
    {
      platform: "Microsoft Azure",
      color: "bg-secondary",
      services: [
        {
          icon: Zap,
          name: "Compute Services",
          description:
            "Virtual machines, app services, and serverless computing",
        },
        {
          icon: Database,
          name: "Data Services",
          description:
            "SQL databases, Cosmos DB, Data Factory, Synapse Analytics",
        },
        {
          icon: Settings,
          name: "DevOps",
          description:
            "CI/CD pipelines, source control, and project management",
        },
        {
          icon: Shield,
          name: "Security Center",
          description:
            "Threat protection, compliance management, security monitoring",
        },
        {
          icon: Building,
          name: "Azure Architect",
          description:
            "Cloud architecture design, solution blueprints, and enterprise frameworks",
        },
      ],
    },
    {
      platform: "Amazon Web Services (AWS)",
      color: "bg-primary",
      services: [
        {
          icon: Globe,
          name: "EC2 & Compute",
          description: "Elastic compute, auto-scaling, and load balancing",
        },
        {
          icon: Database,
          name: "Data & Analytics",
          description: "RDS, DynamoDB, Redshift, EMR, and data lakes",
        },
        {
          icon: Zap,
          name: "Lambda & Serverless",
          description: "Function as a service and event-driven computing",
        },
        {
          icon: Settings,
          name: "DevOps & Automation",
          description: "CodePipeline, CloudFormation, infrastructure as code",
        },
        {
          icon: Snowflake,
          name: "AWS Snowflake",
          description:
            "Cloud data platform for analytics, data warehousing, and AI/ML workloads",
        },
      ],
    },
    {
      platform: "MuleSoft Anypoint Platform",
      color: "bg-secondary",
      services: [
        {
          icon: Zap,
          name: "API-Led Connectivity",
          description:
            "Connect applications, data, and devices with reusable APIs and integrations",
        },
        {
          icon: Database,
          name: "Data Integration",
          description:
            "Real-time data synchronization, ETL, and 350+ pre-built connectors",
        },
        {
          icon: Cloud,
          name: "CloudHub iPaaS",
          description:
            "Fully managed cloud integration platform with auto-scaling and global deployment",
        },
        {
          icon: Shield,
          name: "API Management",
          description:
            "Secure, govern, and monitor APIs with comprehensive policy enforcement",
        },
        {
          icon: Settings,
          name: "RPA & Automation",
          description:
            "Automate repetitive tasks and integrate legacy systems with modern workflows",
        },
      ],
    },
  ];

  const migrationPhases = [
    {
      phase: "Assessment & Planning",
      duration: "2-4 weeks",
      activities: [
        "Infrastructure audit",
        "Workload analysis",
        "Cost optimization planning",
        "Migration strategy design",
      ],
    },
    {
      phase: "Architecture & Design",
      duration: "3-6 weeks",
      activities: [
        "Cloud architecture design",
        "Security framework",
        "Network topology",
        "Disaster recovery planning",
      ],
    },
    {
      phase: "Migration Execution",
      duration: "8-20 weeks",
      activities: [
        "Phased migration",
        "Data synchronization",
        "Application modernization",
        "Integration testing",
      ],
    },
    {
      phase: "Optimization & Support",
      duration: "Ongoing",
      activities: [
        "Performance tuning",
        "Cost optimization",
        "Security hardening",
        "24/7 monitoring",
      ],
    },
  ];

  const benefits = [
    { metric: "Cost Reduction", value: "Up to 70%", icon: TrendingUp },
    { metric: "Uptime SLA", value: "99.9%", icon: Cloud },
    { metric: "Deployment Speed", value: "5x Faster", icon: Zap },
    { metric: "Security", value: "Enterprise-grade", icon: Lock },
  ];

  return (
    <>
      <SEOHead
        title="Cloud Platform Services - OCI, Azure, AWS | Smartborn"
        description="Expert cloud migration and optimization services for Oracle Cloud Infrastructure (OCI), Microsoft Azure, and Amazon Web Services (AWS). Transform your infrastructure with certified cloud consultants."
        keywords="cloud migration, OCI, Oracle Cloud, Microsoft Azure, AWS, cloud optimization, cloud security, infrastructure modernization"
        canonical="/cloud-platforms-detail"
      />

      <div className="min-h-screen bg-background">
        <Navigation />

        <main className="pt-16">
          {/* Hero Section */}
          <section className="py-20 bg-gradient-to-br from-background via-background to-primary/5">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <Badge className="mb-6 bg-secondary/10 text-secondary border-secondary/20">
                  Multi-Cloud Experts
                </Badge>
                <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-primary bg-clip-text text-transparent">
                  Cloud Platform Services
                </h1>
                <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                  Accelerate your digital transformation with expert cloud
                  migration, optimization, and management across OCI, Azure, and
                  AWS platforms.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    size="lg"
                    onClick={handleGetStarted}
                    className="bg-gradient-primary hover:opacity-90"
                  >
                    Start Cloud Journey
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  {/* <Button
                    size="lg"
                    variant="outline"
                    onClick={() => navigate("/")}
                  >
                    View All Solutions
                  </Button> */}
                </div>
              </div>
            </div>
          </section>

          {/* Migration Methodology */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Cloud Migration Methodology
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Proven framework for seamless cloud migration with minimal
                  downtime and maximum ROI
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {migrationPhases.map((phase, index) => (
                  <Card
                    key={index}
                    className="relative bg-gradient-card border-0 shadow-elegant"
                  >
                    <CardHeader className="text-center pb-4">
                      <div className="bg-gradient-primary text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                        {index + 1}
                      </div>
                      <CardTitle className="text-lg font-semibold text-foreground">
                        {phase.phase}
                      </CardTitle>
                      <Badge variant="outline" className="mx-auto">
                        {phase.duration}
                      </Badge>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {phase.activities.map((activity, idx) => (
                          <li
                            key={idx}
                            className="flex items-start gap-2 text-sm text-muted-foreground"
                          >
                            <CheckCircle className={`h-4 w-4 ${idx % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-0.5 flex-shrink-0`} />
                            {activity}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Cloud Platform Services */}
          <section className="py-20 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Multi-Cloud Expertise
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Comprehensive services across leading cloud platforms
                </p>
              </div>

              <div className="space-y-12">
                {cloudServices.map((platform, platformIndex) => (
                  <div key={platformIndex} className="space-y-6">
                    <div className="text-center">
                      <div
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-white ${platform.color}`}
                      >
                        <Server className="h-5 w-5" />
                        <span className="font-semibold">
                          {platform.platform}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                      {platform.services.map((service, serviceIndex) => (
                        <Card
                          key={serviceIndex}
                          className="group hover:shadow-elegant transition-all duration-300 bg-gradient-card border-0"
                        >
                          <CardContent className="p-6">
                            <div className="text-center">
                              <div className="bg-gradient-primary p-3 rounded-lg group-hover:shadow-glow transition-all duration-300 inline-flex mb-4">
                                <service.icon className="h-6 w-6 text-white" />
                              </div>
                              <h3 className="font-semibold text-foreground mb-3 text-lg">
                                {service.name}
                              </h3>
                              <p className="text-muted-foreground text-sm leading-relaxed">
                                {service.description}
                              </p>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Cloud Benefits */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Cloud Transformation Benefits
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Tangible business outcomes from strategic cloud adoption
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {benefits.map((benefit, index) => (
                  <Card
                    key={index}
                    className="text-center bg-gradient-card border-0 shadow-elegant"
                  >
                    <CardContent className="p-8">
                      <benefit.icon className={`h-12 w-12 ${index % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mx-auto mb-4`} />
                      <h3 className="font-semibold text-foreground mb-2 text-lg">
                        {benefit.metric}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {benefit.value}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-gradient-to-br from-primary/5 via-secondary/5 to-background">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Ready to Accelerate Your Cloud Journey?
                </h2>
                <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                  Partner with certified cloud experts to unlock the full
                  potential of OCI, Azure, and AWS for your organization.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    size="lg"
                    onClick={handleGetStarted}
                    className="bg-gradient-primary hover:opacity-90"
                  >
                    Get Cloud Assessment
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  {/* <Button
                    size="lg"
                    variant="outline"
                    onClick={() => navigate("/")}
                  >
                    Explore More Solutions
                  </Button> */}
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default CloudPlatformsDetail;
